import type { ApiResponse, ErrorResponse } from "@/lib/api/types";
export { apiClient } from "@/lib/api/client";
import { apiClient } from "@/lib/api/client";
import { API_PREFIX } from "@/lib/constants/api";

/** Canonical Intake REST prefix (`/api/v1/intake`). */
export const intakeApiBase = `${API_PREFIX}/intake`;
export const intakeAdminApiBase = `${intakeApiBase}/admin`;
/** Work Orchestration REST prefix (`/api/v1/work`). */
export const workApiBase = `${API_PREFIX}/work`;
/** Commercial Party REST prefix (`/api/v1/parties`) — masters, binding, projections. */
export const partiesApiBase = `${API_PREFIX}/parties`;
/** Governance REST prefix (`/api/v1/governance`). */
export const governanceApiBase = `${API_PREFIX}/governance`;
export const governanceAdminApiBase = `${governanceApiBase}/admin`;
export const authzBase = `${API_PREFIX}/authz`;
export const contractsBase = `${API_PREFIX}/contracts`;
export const financeBase = `${API_PREFIX}/finance`;
export const intake = intakeApiBase;
export const work = workApiBase;
export const parties = partiesApiBase;
export const admin = intakeAdminApiBase;
export const governance = governanceApiBase;
export const governanceAdmin = governanceAdminApiBase;
export const authz = authzBase;
export const contracts = contractsBase;
export const finance = financeBase;

export type AnalyticsReportKind = "productivity" | "quality" | "project-effort";
export type AnalyticsReportFormat = "excel" | "pdf" | "csv";

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export function analyticsReportSegment(kind: AnalyticsReportKind): string {
  if (kind === "productivity") return "productivity";
  if (kind === "quality") return "quality";
  return "project-effort";
}

export async function downloadReportFile(apiPath: string): Promise<DownloadedFile | null> {
  try {
    const file = await apiClient.getDownloadedFile(apiPath);
    const filename = file.filename !== null && file.filename.length > 0 ? file.filename : "download";
    return { blob: file.blob, filename };
  } catch {
    return null;
  }
}

export const qs = (params: Record<string, unknown>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      v.forEach((x) => sp.append(k, String(x)));
      return;
    }
    sp.set(k, String(v));
  });
  const out = sp.toString();
  return out ? `?${out}` : "";
};

export const SHARED_AUTHZ_READ_TTL_MS = 5 * 60 * 1000;
export const SHARED_DIRECTORY_READ_TTL_MS = 60 * 1000;
export const SHARED_TASK_OPTIONS_READ_TTL_MS = 60 * 1000;
export const SHARED_SETTINGS_READ_TTL_MS = 60 * 1000;
export const SHARED_PAGE_READ_TTL_MS = 1500;

export type SharedReadOptions = {
  force?: boolean;
  includeTenantMemberships?: boolean;
  /** When false, skip the session pin and load from the network. Settled memory TTL still applies unless force. */
  swr?: boolean;
  /** Keep this HTTP status in the page TTL. Other failures stay uncached. */
  retainFailureStatus?: number;
};

type SharedReadCacheEntry<T> = {
  createdAt: number;
  request: Promise<ApiResponse<T>>;
  /** True until the underlying load() settles — concurrent force/TTL-miss join this. */
  inFlight: boolean;
  /** Captured when the load started. A later authz clear must not join or republish it. */
  generation: number;
};

const sharedReadCache = new Map<string, SharedReadCacheEntry<unknown>>();
const AUTHZ_SESSION_STORAGE_PREFIX = "9s:authz-shared-read:";
let sharedReadGeneration = 0;

type StoredAuthzSharedRead<T> = {
  storedAt: number;
  response: ApiResponse<T>;
};

function authzSessionStorageKey(key: string): string {
  return `${AUTHZ_SESSION_STORAGE_PREFIX}${key}`;
}

function readAuthzSessionSharedRead<T>(key: string, ttlMs: number): StoredAuthzSharedRead<T> | null {
  if (!key.startsWith("authz::")) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(authzSessionStorageKey(key));
    if (raw === null) {
      return null;
    }
    const stored = JSON.parse(raw) as StoredAuthzSharedRead<T>;
    if (Date.now() - stored.storedAt >= ttlMs) {
      sessionStorage.removeItem(authzSessionStorageKey(key));
      return null;
    }
    if (!stored.response.success) {
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

function writeAuthzSessionSharedRead<T>(key: string, response: ApiResponse<T>): void {
  if (!key.startsWith("authz::")) {
    return;
  }
  if (!response.success) {
    return;
  }
  try {
    const stored: StoredAuthzSharedRead<T> = { storedAt: Date.now(), response };
    sessionStorage.setItem(authzSessionStorageKey(key), JSON.stringify(stored));
  } catch {
    /* private mode or quota */
  }
}

function clearAuthzSessionSharedRead(scope?: "authz"): void {
  const prefix = scope === "authz" ? AUTHZ_SESSION_STORAGE_PREFIX : "";
  if (prefix.length === 0) {
    return;
  }
  const keysToRemove: string[] = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key !== null && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    sessionStorage.removeItem(key);
  }
}

export function sharedReadCacheKey(scope: string, parts: readonly string[] = []): string {
  return [scope, ...parts.map((part) => part.trim())].join("::");
}

export function sharedReadParamParts(params?: Record<string, unknown>): string[] {
  if (params === undefined) {
    return [];
  }
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([key, value]) => {
      if (!Array.isArray(value)) {
        return [`${key}=${String(value)}`];
      }
      return value.map((item) => `${key}=${String(item)}`);
    });
}

export type SharedReadLoad<T> = () => Promise<ApiResponse<T>>;

function isErrorResponse<T>(response: ApiResponse<T>): response is ErrorResponse {
  return response.success === false;
}

function retainsSharedReadFailure<T>(response: ApiResponse<T>, options?: SharedReadOptions): boolean {
  if (!isErrorResponse(response)) {
    return false;
  }
  const status = options?.retainFailureStatus;
  if (status === undefined) {
    return false;
  }
  return response.error.details?.status === status;
}

export function getSharedRead<T>(
  key: string,
  ttlMs: number,
  load: SharedReadLoad<T>,
  options?: SharedReadOptions,
): Promise<ApiResponse<T>> {
  const now = Date.now();
  const existing = sharedReadCache.get(key) as SharedReadCacheEntry<T> | undefined;
  // Join only a load from the current generation so a tenant switch cannot reuse the previous org.
  if (existing?.inFlight && existing.generation === sharedReadGeneration) {
    return existing.request;
  }
  if (!options?.force) {
    if (existing && !existing.inFlight && existing.generation === sharedReadGeneration && now - existing.createdAt < ttlMs) {
      return existing.request;
    }
    const sessionStored = options?.swr === false ? null : readAuthzSessionSharedRead<T>(key, ttlMs);
    if (sessionStored !== null) {
      const generation = sharedReadGeneration;
      const sessionPromise = Promise.resolve(sessionStored.response);
      sharedReadCache.set(key, {
        createdAt: now,
        request: sessionPromise as Promise<ApiResponse<unknown>>,
        inFlight: false,
        generation,
      });
      void load()
        .then((response) => {
          if (generation !== sharedReadGeneration) {
            return response;
          }
          if (response.success) {
            writeAuthzSessionSharedRead(key, response);
            sharedReadCache.set(key, {
              createdAt: Date.now(),
              request: Promise.resolve(response) as Promise<ApiResponse<unknown>>,
              inFlight: false,
              generation,
            });
            return response;
          }
          const failed = sharedReadCache.get(key) as SharedReadCacheEntry<T> | undefined;
          if (failed && failed.generation === generation) {
            sharedReadCache.delete(key);
          }
          return response;
        })
        .catch((error) => {
          const failed = sharedReadCache.get(key) as SharedReadCacheEntry<T> | undefined;
          if (failed && failed.generation === generation) {
            sharedReadCache.delete(key);
          }
          throw error;
        });
      return sessionPromise;
    }
  }
  // force=true skips TTL for settled entries only; concurrent callers join via inFlight above.
  const generation = sharedReadGeneration;
  const request = load()
    .then((response) => {
      const entry = sharedReadCache.get(key) as SharedReadCacheEntry<T> | undefined;
      const ownsEntry = entry !== undefined && entry.request === request;
      if (generation !== sharedReadGeneration) {
        if (ownsEntry) {
          sharedReadCache.delete(key);
        }
        return response;
      }
      if (ownsEntry) {
        entry.inFlight = false;
        entry.createdAt = Date.now();
      }
      if (response.success) {
        writeAuthzSessionSharedRead(key, response);
      } else if (ownsEntry && !retainsSharedReadFailure(response, options)) {
        sharedReadCache.delete(key);
      }
      return response;
    })
    .catch((error) => {
      const entry = sharedReadCache.get(key) as SharedReadCacheEntry<T> | undefined;
      if (entry && entry.request === request) {
        sharedReadCache.delete(key);
      }
      throw error;
    });
  sharedReadCache.set(key, {
    createdAt: now,
    request: request as Promise<ApiResponse<unknown>>,
    inFlight: true,
    generation,
  });
  return request;
}

export function clearSharedReadCache(scope?: "authz" | "directory" | "task_options" | "page_read"): void {
  if (!scope || scope === "authz") {
    sharedReadGeneration += 1;
  }
  if (!scope) {
    sharedReadCache.clear();
    clearAuthzSessionSharedRead("authz");
    return;
  }
  const prefix = `${scope}::`;
  for (const key of sharedReadCache.keys()) {
    if (key === scope || key.startsWith(prefix)) {
      sharedReadCache.delete(key);
    }
  }
  if (scope === "authz") {
    clearAuthzSessionSharedRead("authz");
  }
}

export function clearProjectReadCache(): void {
  clearSharedReadCache("page_read");
  clearSharedReadCache("task_options");
}

export function clearSharedReadCacheKeys(keys: string[]): void {
  for (const key of keys) {
    sharedReadCache.delete(key);
  }
}

export function clearSharedReadCacheKeyPrefixes(prefixes: string[]): void {
  for (const key of sharedReadCache.keys()) {
    for (const prefix of prefixes) {
      if (key === prefix) {
        sharedReadCache.delete(key);
        break;
      }
      if (key.startsWith(`${prefix}::`)) {
        sharedReadCache.delete(key);
        break;
      }
    }
  }
}

export function clearTaskDetailReadCache(taskId?: string): void {
  const normalizedTaskId = String(taskId ?? "").trim();
  if (!normalizedTaskId) {
    clearSharedReadCacheKeyPrefixes([sharedReadCacheKey("page_read", ["task"])]);
    return;
  }
  clearSharedReadCacheKeyPrefixes([sharedReadCacheKey("page_read", ["task", normalizedTaskId])]);
}

export function clearTasksListReadCache(): void {
  clearSharedReadCacheKeyPrefixes([
    sharedReadCacheKey("page_read", ["tasks"]),
    sharedReadCacheKey("page_read", ["tasks-summary"]),
  ]);
}

export function clearTaskMutationReadCache(taskId?: string): void {
  clearTaskDetailReadCache(taskId);
  clearTasksListReadCache();
}

export function clearRecordReadCache(recordId?: string): void {
  const normalizedRecordId = String(recordId ?? "").trim();
  if (!normalizedRecordId) {
    clearSharedReadCacheKeyPrefixes([
      sharedReadCacheKey("page_read", ["record"]),
      sharedReadCacheKey("page_read", ["triage"]),
      sharedReadCacheKey("page_read", ["draft"]),
      sharedReadCacheKey("page_read", ["record-pipeline"]),
    ]);
    return;
  }
  clearSharedReadCacheKeyPrefixes([
    sharedReadCacheKey("page_read", ["record", normalizedRecordId]),
    sharedReadCacheKey("page_read", ["triage", normalizedRecordId]),
    sharedReadCacheKey("page_read", ["draft", normalizedRecordId]),
    sharedReadCacheKey("page_read", ["record-pipeline", normalizedRecordId]),
  ]);
}

export function clearProjectDetailReadCache(projectId?: string): void {
  const normalizedProjectId = String(projectId ?? "").trim();
  if (!normalizedProjectId) {
    clearSharedReadCacheKeyPrefixes([sharedReadCacheKey("page_read", ["project"])]);
    return;
  }
  clearSharedReadCacheKeyPrefixes([sharedReadCacheKey("page_read", ["project", normalizedProjectId])]);
}

export function clearClientReadCache(clientId?: string): void {
  clearSharedReadCacheKeyPrefixes([
    sharedReadCacheKey("page_read", ["customers"]),
    sharedReadCacheKey("page_read", ["customer"]),
    sharedReadCacheKey("page_read", ["parties"]),
    sharedReadCacheKey("page_read", ["party"]),
    sharedReadCacheKey("page_read", ["clients"]),
    sharedReadCacheKey("page_read", ["client"]),
  ]);
  const normalizedClientId = String(clientId ?? "").trim();
  if (normalizedClientId) {
    clearSharedReadCacheKeyPrefixes([
      sharedReadCacheKey("page_read", ["customer", normalizedClientId]),
      sharedReadCacheKey("page_read", ["party", normalizedClientId]),
      sharedReadCacheKey("page_read", ["client", normalizedClientId]),
    ]);
  }
}

export function clearNotificationReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["notification-summary"]),
    sharedReadCacheKey("page_read", ["notification-schedule-status"]),
  ]);
}


export function clearLlmCatalogReadCache(): void {
  clearSharedReadCacheKeyPrefixes([
    sharedReadCacheKey("page_read", ["admin-llm-catalog-providers"]),
    sharedReadCacheKey("page_read", ["admin-llm-catalog-models"]),
  ]);
}

export function clearAiAuditReadCache(): void {
  clearSharedReadCacheKeyPrefixes([
    sharedReadCacheKey("page_read", ["admin-ai-audit-summary"]),
    sharedReadCacheKey("page_read", ["admin-ai-audit-entries"]),
    sharedReadCacheKey("page_read", ["admin-ai-audit-detail"]),
  ]);
}

export function clearLlmPrivacyReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["admin-llm-privacy-rules"]),
  ]);
}

export function clearPromptTemplateReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["prompt-templates"]),
    sharedReadCacheKey("page_read", ["prompt-template-builtin-defaults"]),
    sharedReadCacheKey("page_read", ["prompt-kinds"]),
  ]);
}

export function clearSettingsMeReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["settings-me"]),
    sharedReadCacheKey("page_read", ["settings-me-profile"]),
  ]);
}

export function clearAdminSettingsReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["admin-settings-integrations"]),
    sharedReadCacheKey("page_read", ["admin-settings-operational"]),
    sharedReadCacheKey("page_read", ["admin-settings-operational", "view=full"]),
    sharedReadCacheKey("page_read", ["admin-settings-operational", "view=sync"]),
  ]);
}

export function clearFinanceReadCache(): void {
  clearSharedReadCacheKeyPrefixes([sharedReadCacheKey("page_read", ["finance"])]);
}

/** Drop org-scoped shared reads after active-tenant switch. */
export function clearTenantScopedReadCache(): void {
  clearFinanceReadCache();
  clearSharedReadCache("page_read");
  clearSharedReadCache("task_options");
  // Nav policy snapshot is tenant-scoped — must not reuse Platform resource_items.
  clearSharedReadCache("authz");
}

export function clearAdminRuleContractReadCache(): void {
  clearSharedReadCacheKeys([
    sharedReadCacheKey("page_read", ["admin-rule-contract"]),
  ]);
}

